import { pushApi } from './api/client';

/**
 * Check if push notifications are supported in this browser
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Check if push notifications are currently enabled (subscribed)
 */
export async function isPushEnabled(): Promise<boolean> {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) return false;

    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch {
    return false;
  }
}

/**
 * Get the current notification permission state
 */
export function getPermissionState(): NotificationPermission {
  if (!isPushSupported()) return 'denied';
  return Notification.permission;
}

/**
 * Register the service worker and subscribe to push notifications.
 * Returns true if successfully subscribed.
 */
export async function registerPushSubscription(): Promise<boolean> {
  if (!isPushSupported()) {
    console.warn('[push] Push notifications not supported in this browser');
    return false;
  }

  // Request notification permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.warn('[push] Notification permission denied');
    return false;
  }

  // Register service worker
  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;

  // Get VAPID key from server
  const { publicKey } = await pushApi.getVapidKey();

  // Convert VAPID key from base64url to Uint8Array
  const vapidKey = urlBase64ToUint8Array(publicKey);

  // Subscribe to push
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: vapidKey.buffer as ArrayBuffer,
  });

  // Send subscription to server
  const raw = subscription.toJSON();
  await pushApi.subscribe({
    endpoint: raw.endpoint!,
    keys: {
      p256dh: raw.keys!.p256dh!,
      auth: raw.keys!.auth!,
    },
  });

  return true;
}

/**
 * Unsubscribe from push notifications
 */
export async function unregisterPushSubscription(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!registration) return;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  // Notify server
  await pushApi.unsubscribe(subscription.endpoint);

  // Unsubscribe locally
  await subscription.unsubscribe();
}

/**
 * Convert a base64url-encoded string to a Uint8Array (for VAPID key)
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const outputArray = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    outputArray[i] = raw.charCodeAt(i);
  }
  return outputArray;
}
